import { useMemo, ReactElement, Fragment, useCallback } from 'react';

import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListSubheader,
  Typography,
} from '@material-ui/core';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useLocation, matchPath } from 'react-router-dom';

import RoleGuard from 'src/components/RoleGuard';
import { RoleGuardProps } from 'src/components/RoleGuard/types';
import { COGNITO_GROUPS_MAP, NAV_SECTIONS, NavSection } from 'src/constants';
import { useAuth } from 'src/hooks';
import { getConcatString } from 'src/utils';

import { useStyles } from './index.style';
import NavItem from './NavItem';
import { ChildRoutesProps, NavBarProps } from './types';
import packageJson from '../../../../package.json';

const CURRENT_YEAR = new Date().getFullYear();

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth,
  collapsed,
}: ChildRoutesProps) {
  const key = item.title + depth;

  const isActive = item.href && pathname.includes(item.href);

  const Wrapper = item.requiredPermissions ? RoleGuard : Fragment;

  const wrapperProps = item.requiredPermissions
    ? ({
        requiredPermissions: item.requiredPermissions,
        disabledBehavior: 'hide',
        requirementType: 'any',
      } as RoleGuardProps)
    : {};

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <Wrapper key={key} {...wrapperProps}>
        <NavItem
          collapsed={collapsed}
          depth={depth}
          icon={item.icon}
          info={item.info}
          key={key}
          open={Boolean(open)}
          title={item.title}
          isActive={!!isActive}
        >
          <List disablePadding>
            {item.items.reduce(
              (result, el) =>
                reduceChildRoutes({
                  acc: result,
                  item: el,
                  pathname,
                  depth: depth + 1,
                  collapsed,
                }),
              [] as Array<ReactElement>,
            )}
          </List>
        </NavItem>
      </Wrapper>,
    );
  } else {
    acc.push(
      <Wrapper key={key} {...wrapperProps}>
        <NavItem
          collapsed={collapsed}
          depth={depth}
          href={item.href}
          icon={item.icon}
          info={item.info}
          key={key}
          title={item.title}
          isActive={!!isActive}
        />
      </Wrapper>,
    );
  }

  return acc;
}

const NavBar = ({ open, onToggle }: NavBarProps) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();
  const { familyName, givenName, cognitoGroups } = user || {};

  const userName = useMemo(
    () => getConcatString([givenName, familyName]),
    [familyName, givenName],
  );

  const roles = useMemo(() => {
    const validGroupsList: Array<string> = [];

    cognitoGroups?.forEach((group) => {
      if (Object.keys(COGNITO_GROUPS_MAP).includes(group)) {
        validGroupsList.push(COGNITO_GROUPS_MAP[group as unknown as keyof typeof COGNITO_GROUPS_MAP]);
      }
    });

    return getConcatString(validGroupsList);
  }, [cognitoGroups]);

  const renderSection = useCallback(
    (section: NavSection, collapsed: boolean) => {
      const Wrapper = section.requiredPermissions ? RoleGuard : Fragment;

      const wrapperProps = section.requiredPermissions
        ? ({
            requiredPermissions: section.requiredPermissions,
            disabledBehavior: 'hide',
            requirementType: 'any',
          } as RoleGuardProps)
        : {};

      return (
        <Wrapper key={section.subheader} {...wrapperProps}>
          <List
            key={section.subheader}
            subheader={
              !collapsed ? (
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              ) : undefined
            }
          >
            <List disablePadding>
              {section.items.reduce(
                (acc, item) =>
                  reduceChildRoutes({
                    acc,
                    item,
                    pathname: location.pathname,
                    depth: 0,
                    collapsed,
                  }),
                [] as Array<ReactElement>,
              )}
            </List>
          </List>
        </Wrapper>
      );
    },
    [location.pathname],
  );

  const renderContent = (collapsed: boolean) => (
    <Box flexGrow={1} minHeight={0} display="flex" flexDirection="column">
      <PerfectScrollbar
        className={classes.perfectScrollbar}
        options={{ suppressScrollX: true }}
      >
        {!collapsed && (
          <Box p={2}>
            <Box display="flex" justifyContent="center">
              {/* - Removing this link until user profile is implemented.
              <RouterLink to={USER_PROFILE_VIEW}>
                <Avatar alt="User" className={classes.avatar} src="" />
              </RouterLink>
              */}
              <Avatar alt="User" className={classes.avatar} src="" />
            </Box>
            <Box mt={2} textAlign="center">
              {/* - Removing this link until user profile is implemented.
              <Link
                component={RouterLink}
                to={USER_PROFILE_VIEW}
                variant="h5"
                color="textPrimary"
                underline="none"
              >
                {userName}
              </Link>
              */}
              <Typography variant="h5" color="textPrimary">
                {userName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {roles}
              </Typography>
            </Box>
          </Box>
        )}
        <Divider />
        <Box p={collapsed ? 1 : 2}>
          {NAV_SECTIONS.map((section) => renderSection(section, collapsed))}
        </Box>
        <Box mt="auto">
          <Divider />
          {!collapsed && (
            <Box>
              <Box display="flex" justifyContent="center">
                <Typography variant="caption" color="textSecondary">
                  v{packageJson.version}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center">
                <Typography variant="caption" color="textSecondary">
                  © Copyright {CURRENT_YEAR} CHEF iQ®
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center">
                <Typography variant="caption" color="textSecondary">
                  ALL THE RIGHTS RESERVED
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      classes={{
        paper: clsx(classes.drawer, {
          [classes.drawerCollapsed]: !open,
        }),
      }}
      variant="permanent"
    >
      <Box
        display="flex"
        justifyContent={open ? 'flex-end' : 'center'}
        px={1}
        py={0.5}
      >
        <IconButton
          aria-label="toggle sidebar"
          onClick={onToggle}
          size="small"
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      {renderContent(!open)}
    </Drawer>
  );
};

export default NavBar;
